import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  ArrowLeft,
  Check,
  X,
  Star,
  Trophy,
  Flame,
  Loader2,
  ListChecks,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import { Sentry } from '../lib/sentry';
import { Question, QuestionAttempt } from '../interfaces/course';
import { TestTimer } from './test-timer';
import { ConfirmModal } from './confirm-modal';

type QuestionViewProps = {
  questions: Question[];
  backUrl: string;
  exitConfirm?: {
    title: string;
    description: string;
  };
  shouldOpenExitModal?: boolean;
  onExitModalDismiss?: () => void;
  onComplete: (attempts: QuestionAttempt[]) => Promise<boolean>;
  onExit?: (attempts: QuestionAttempt[]) => void;
  onTimeOut?: (attempts: QuestionAttempt[]) => void;
  secondsRemaining?: number;
};

const MotionButton = motion(Button);
const MotionInput = motion(Input);

const optionVariants = {
  initial: { scale: 0.8, opacity: 0 },
  animate: (i: number) => ({
    scale: 1,
    opacity: 1,
    transition: {
      delay: i * 0.1,
      type: 'spring',
      stiffness: 200,
    },
  }),
  exit: {
    x: -100,
    opacity: 0,
    transition: { duration: 0.2 },
  },
  hover: {
    scale: 1.02,
    transition: { type: 'spring', stiffness: 400 },
  },
  tap: { scale: 0.98 },
};

const sparkleVariants = {
  initial: { scale: 0, opacity: 0 },
  animate: (i: number) => ({
    scale: [0, 1, 0],
    opacity: [0, 1, 0],
    x: [0, (i % 2 === 0 ? 50 : -50) * (1 + (i % 3))],
    y: [0, -50 - (i % 3) * 30],
    transition: {
      duration: 1,
      delay: i * 0.1,
      ease: 'easeOut',
    },
  }),
};

export function QuestionView({
  questions: initialQuestions,
  backUrl,
  exitConfirm,
  shouldOpenExitModal,
  onExitModalDismiss,
  onComplete,
  onExit,
  onTimeOut,
  secondsRemaining,
}: QuestionViewProps) {
  const navigate = useNavigate();

  const [questions, setQuestions] = useState(initialQuestions);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | string[]>('');
  const [isAnswerSubmitted, setIsAnswerSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState<Set<string>>(new Set());
  const [isChecking, setIsChecking] = useState(false);
  const [streakCount, setStreakCount] = useState(0);
  const [shouldCelebrate, setShouldCelebrate] = useState(false);
  const [showStreakInfo, setShowStreakInfo] = useState(false);
  const [questionProgress, setQuestionProgress] = useState<QuestionAttempt[]>(
    [],
  );
  const questionProgressRef = useRef<QuestionAttempt[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [feedbackType, setFeedbackType] = useState<
    'correct' | 'incorrect' | null
  >(null);

  const currentQuestion = questions[currentQuestionIndex];
  const progress = (correctAnswers.size / initialQuestions.length) * 100;
  const allQuestionsCorrect = correctAnswers.size === initialQuestions.length;

  useEffect(() => {
    if (isCorrect && isAnswerSubmitted) {
      const newStreak = streakCount + 1;
      setStreakCount(newStreak);

      if (newStreak >= 3 && !shouldCelebrate) {
        setShouldCelebrate(true);

        const timer = setTimeout(() => {
          setShouldCelebrate(false);
        }, 2000);

        return () => clearTimeout(timer);
      }
    } else if (!isCorrect && isAnswerSubmitted) {
      setStreakCount(0);
      setShouldCelebrate(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCorrect, isAnswerSubmitted]);

  // Reset question progress when questions change
  useEffect(() => {
    questionProgressRef.current = [];
    setQuestionProgress([]);
  }, [initialQuestions]);

  const handleOptionClick = (option: string) => {
    if (isAnswerSubmitted) return;

    if (currentQuestion.type === 'multipleSelect') {
      setSelectedAnswer((prev) => {
        const prevArray = Array.isArray(prev) ? prev : [];
        return prevArray.includes(option)
          ? prevArray.filter((item) => item !== option)
          : [...prevArray, option];
      });
    } else {
      setSelectedAnswer(option);
    }
  };

  const handleSubmit = async () => {
    if (!selectedAnswer || isChecking) return;
    setIsChecking(true);

    try {
      let correct = false;
      let parsedCorrectAnswer;

      if (typeof currentQuestion.correct_answer === 'string') {
        try {
          parsedCorrectAnswer = JSON.parse(currentQuestion.correct_answer);
        } catch {
          parsedCorrectAnswer = currentQuestion.correct_answer;
        }
      } else {
        parsedCorrectAnswer = currentQuestion.correct_answer;
      }

      if (currentQuestion.type === 'multipleSelect') {
        const selectedIndices = currentQuestion.options
          .map((option: string, index: number) =>
            (selectedAnswer as string[]).includes(option) ? index : null,
          )
          .filter((index: number | null): index is number => index !== null)
          .sort();

        const correctIndices = (
          Array.isArray(parsedCorrectAnswer)
            ? parsedCorrectAnswer
            : JSON.parse(parsedCorrectAnswer)
        ).sort();

        correct =
          JSON.stringify(selectedIndices) === JSON.stringify(correctIndices);
      } else if (currentQuestion.type === 'singleSelect') {
        const selectedIndex = currentQuestion.options.indexOf(
          selectedAnswer as string,
        );
        const correctIndex = Array.isArray(parsedCorrectAnswer)
          ? parsedCorrectAnswer[0]
          : parseInt(parsedCorrectAnswer);
        correct = selectedIndex === correctIndex;
      } else {
        const normalizedCorrect = String(parsedCorrectAnswer)
          .toLowerCase()
          .trim();
        const normalizedSelected = (selectedAnswer as string)
          .toLowerCase()
          .trim();
        correct = normalizedSelected === normalizedCorrect;
      }

      setIsCorrect(correct);

      const attemptRecord: QuestionAttempt = {
        question_id: parseInt(currentQuestion.id, 10),
        is_correct: correct,
        answered_at: new Date().toISOString(),
      };

      questionProgressRef.current = [
        ...questionProgressRef.current,
        attemptRecord,
      ];
      setQuestionProgress(questionProgressRef.current);

      if (correct) {
        const newCorrectAnswers = new Set(correctAnswers);
        newCorrectAnswers.add(currentQuestion.id);
        setCorrectAnswers(newCorrectAnswers);
      }

      setIsAnswerSubmitted(true);
      setFeedbackType(correct ? 'correct' : 'incorrect');
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setIsChecking(false);
    }
  };

  const saveProgress = async () => {
    if (isSaving) return false;
    setIsSaving(true);

    try {
      return await onComplete(questionProgress);
    } catch (error) {
      Sentry.captureException(error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    // If level complete, save all progress
    if (
      correctAnswers.size === initialQuestions.length &&
      currentQuestionIndex === questions.length - 1
    ) {
      const saved = await saveProgress();

      if (saved) {
        navigate(backUrl);
        return;
      }
    }

    // Continue with normal question progression
    if (!isCorrect) {
      // Filter out questions already answered correctly, keep only unanswered ones
      const unansweredQuestions = questions.filter(
        (question, index) =>
          index !== currentQuestionIndex && !correctAnswers.has(question.id),
      );
      // Put the current wrong question at the end
      const reorderedQuestions = [...unansweredQuestions, currentQuestion];
      setQuestions(reorderedQuestions);
      setCurrentQuestionIndex(0);
    } else {
      const nextIndex = (currentQuestionIndex + 1) % questions.length;
      setCurrentQuestionIndex(nextIndex);
    }

    setSelectedAnswer('');
    setIsAnswerSubmitted(false);
    setIsCorrect(false);
  };

  // Close streak info when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setShowStreakInfo(false);
    if (showStreakInfo) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showStreakInfo]);

  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);

  const performExit = () => {
    if (onExit) {
      onExit(questionProgress);
      return;
    }
    navigate(backUrl);
  };

  const exit = () => {
    if (exitConfirm) {
      setIsExitDialogOpen(true);
      return;
    }
    performExit();
  };

  const confirmExit = () => {
    setIsExitDialogOpen(false);
    performExit();
  };

  const hasTimedOutRef = useRef(false);

  useEffect(() => {
    if (secondsRemaining !== 0) return;
    if (hasTimedOutRef.current) return;
    if (!onTimeOut) return;

    hasTimedOutRef.current = true;
    onTimeOut(questionProgressRef.current);
  }, [secondsRemaining, onTimeOut]);

  // Externally-triggered open (e.g. browser back button intercepted by parent)
  useEffect(() => {
    if (shouldOpenExitModal) setIsExitDialogOpen(true);
  }, [shouldOpenExitModal]);

  const dismissExitModal = () => {
    setIsExitDialogOpen(false);
    onExitModalDismiss?.();
  };

  return (
    <MotionConfig reducedMotion="user">
      {exitConfirm && (
        <ConfirmModal
          isOpen={isExitDialogOpen}
          title={exitConfirm.title}
          description={exitConfirm.description}
          confirmLabel="Exit"
          cancelLabel="Stay"
          onConfirm={confirmExit}
          onCancel={dismissExitModal}
        />
      )}
      <div className="min-h-screen flex flex-col bg-bg-main relative overflow-hidden">
        {/* Celebration Overlay */}
        <AnimatePresence>
          {shouldCelebrate && (
            <motion.div
              className="fixed inset-0 pointer-events-none z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Stars */}
              <div className="absolute inset-0">
                {[...Array(20)].map((_, i) => (
                  <motion.div
                    key={i}
                    custom={i}
                    variants={sparkleVariants}
                    initial="initial"
                    animate="animate"
                    className="absolute left-1/2 top-1/2"
                  >
                    <Star
                      className="w-4 h-4 text-accent-pink"
                      style={{
                        transform: `rotate(${i * 45}deg)`,
                        filter: 'drop-shadow(0 0 4px rgba(250, 206, 229, 0.4))',
                      }}
                    />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Header */}
        <motion.div
          className="sticky top-0 z-10 bg-bg-main pt-4 px-4"
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
        >
          <div className="flex items-center justify-between max-w-lg mx-auto mb-6">
            <button
              onClick={exit}
              className="flex items-center text-title hover:text-accent-purple transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            {/* Progress Bar (now in the same line) */}
            <div className="h-2 w-3/5 bg-bg-chat rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full bg-title rounded-full transition-all duration-300',
                  isCorrect && 'bg-accent-purple',
                )}
                style={{ width: `${progress}%` }}
              />
            </div>

            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowStreakInfo(!showStreakInfo);
                }}
                className={cn(
                  'flex items-center gap-1 p-2 rounded-lg transition-all',
                  'hover:bg-bg-chat',
                  showStreakInfo && 'bg-bg-chat',
                )}
              >
                <Flame
                  className={cn(
                    'w-5 h-5 transition-all',
                    streakCount > 0 ? 'text-accent-orange' : 'text-subheading',
                  )}
                />
                {streakCount > 0 && (
                  <motion.span
                    key={streakCount}
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="font-bold text-sm text-accent-orange"
                  >
                    {streakCount}
                  </motion.span>
                )}
              </button>

              {/* Streak info tooltip */}
              <AnimatePresence>
                {showStreakInfo && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 10 }}
                    transition={{ type: 'spring', duration: 0.2 }}
                    className={cn(
                      'absolute top-full right-0 mt-2 p-4 rounded-xl bg-white shadow-lg border w-64',
                      'text-sm text-subheading z-50',
                    )}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <Flame className="w-5 h-5 text-accent-orange" />
                      </div>
                      <div className="space-y-2">
                        <p className="font-semibold text-gray-900">
                          Answer Streak!
                        </p>
                        <p>
                          Get answers correct in a row to build your streak.
                          Higher streaks unlock special celebrations!
                        </p>
                        {streakCount > 0 && (
                          <div className="mt-3 p-2 bg-orange-50 rounded-lg text-orange-600 font-medium">
                            Current streak: {streakCount}{' '}
                            {streakCount >= 3 ? '🔥' : ''}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {secondsRemaining !== undefined && (
            <div className="flex justify-center max-w-lg mx-auto mb-4">
              <TestTimer secondsRemaining={secondsRemaining} />
            </div>
          )}
        </motion.div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestionIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="flex-1 max-w-lg mx-auto w-full px-4 pb-32"
          >
            <div className="bg-white rounded-3xl shadow-sm p-6 mb-4">
              <h2 className="text-2xl font-bold text-title mb-4">
                {currentQuestion.content}
              </h2>

              <AnimatePresence mode="wait">
                {currentQuestion.type === 'multipleSelect' ? (
                  <div className="text-sm font-bold text-black mb-4 bg-bg-chat px-4 py-2 inline-flex items-center gap-2 rounded-full">
                    <ListChecks className="w-4 h-4" />
                    Select all that apply
                  </div>
                ) : null}

                {/* Fill in the blank input */}
                {currentQuestion.type === 'fillInBlank' && (
                  <div className="mb-6 mt-4">
                    <label className="block text-sm font-medium text-subheading mb-2">
                      Your answer:
                    </label>
                    <MotionInput
                      type="text"
                      value={selectedAnswer as string}
                      onChange={(e) => setSelectedAnswer(e.target.value)}
                      placeholder="Type your answer here"
                      className="w-full p-4 rounded-xl text-base"
                      disabled={isAnswerSubmitted}
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.2 }}
                      onKeyDown={(e) => {
                        if (
                          e.key === 'Enter' &&
                          !isAnswerSubmitted &&
                          selectedAnswer
                        ) {
                          handleSubmit();
                        }
                      }}
                    />
                  </div>
                )}

                {/* Answer Options */}
                <motion.div
                  key={`options-${currentQuestionIndex}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-3"
                >
                  {(currentQuestion.type === 'singleSelect' ||
                    currentQuestion.type === 'multipleSelect') &&
                    currentQuestion.options.map(
                      (option: string, index: number) => (
                        <motion.button
                          key={option}
                          variants={optionVariants}
                          initial="initial"
                          animate="animate"
                          exit="exit"
                          whileHover={!isAnswerSubmitted ? 'hover' : {}}
                          whileTap={!isAnswerSubmitted ? 'tap' : {}}
                          custom={index}
                          disabled={isAnswerSubmitted}
                          onClick={() => handleOptionClick(option)}
                          className={cn(
                            'w-full text-left p-4 rounded-xl border transition-all duration-200',
                            currentQuestion.type === 'multipleSelect'
                              ? Array.isArray(selectedAnswer)
                                ? selectedAnswer.includes(option)
                                  ? isAnswerSubmitted
                                    ? isCorrect
                                      ? 'border-green-500 bg-correct text-title'
                                      : 'border-red-500 bg-red-50 text-title'
                                    : 'border-accent-purple bg-bg-chat text-title'
                                  : 'border-outline'
                                : 'border-outline'
                              : selectedAnswer === option
                                ? isAnswerSubmitted
                                  ? isCorrect
                                    ? 'border-green-500 bg-correct text-title'
                                    : 'border-red-500 bg-red-50 text-title'
                                  : 'border-accent-purple bg-bg-chat text-title'
                                : 'border-outline',
                          )}
                        >
                          <div className="flex items-center">
                            <div className="w-8 h-8 min-w-[2rem] rounded-full flex items-center justify-center mr-3 text-sm font-medium bg-black text-white flex-shrink-0">
                              {String.fromCharCode(65 + index)}
                            </div>
                            <span className="font-medium text-base text-title">
                              {option}
                            </span>
                          </div>
                        </motion.button>
                      ),
                    )}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Fixed Bottom Section with Feedback and Button */}
        <div className="fixed bottom-0 left-0 right-0">
          <div
            className={cn(
              'relative max-w-lg mx-auto overflow-hidden shadow-lg',
              isAnswerSubmitted && feedbackType === 'correct' && 'bg-correct',
              isAnswerSubmitted && feedbackType === 'incorrect' && 'bg-red-50',
            )}
            style={{
              borderTopLeftRadius: '24px',
              borderTopRightRadius: '24px',
            }}
          >
            {/* Feedback Message */}
            {isAnswerSubmitted && feedbackType && (
              <div className="w-full px-6 pt-5 pb-3 text-black">
                <div className="flex items-center">
                  {feedbackType === 'correct' ? (
                    <Check className="text-green-600 mr-2 h-6 w-6" />
                  ) : (
                    <X className="text-red-500 mr-2 h-6 w-6" />
                  )}
                  <h3 className="font-semibold text-black text-lg">
                    {feedbackType === 'correct'
                      ? "That's correct!"
                      : 'Almost there!'}
                  </h3>
                </div>
                <p className="mt-2 text-sm text-black">
                  {currentQuestion.explanation ||
                    (feedbackType === 'correct'
                      ? 'Great job!'
                      : 'Please try again.')}
                </p>
              </div>
            )}

            {/* Button Section */}
            <div className="p-4">
              <AnimatePresence mode="wait">
                {!isAnswerSubmitted ? (
                  <MotionButton
                    key="check"
                    className="w-full h-12 text-white font-medium text-lg rounded-full bg-accent-purple hover:bg-accent-purple/90 py-6 flex items-center justify-center"
                    onClick={handleSubmit}
                    disabled={!selectedAnswer || isChecking}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {isChecking ? (
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin text-white" />
                        Checking...
                      </div>
                    ) : (
                      'Check Answer'
                    )}
                  </MotionButton>
                ) : (
                  <MotionButton
                    key="next"
                    className="w-full h-12 text-white font-medium text-lg rounded-full bg-accent-purple hover:bg-accent-purple/90 py-6 flex items-center justify-center"
                    onClick={handleNext}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {allQuestionsCorrect &&
                    currentQuestionIndex === questions.length - 1 ? (
                      <motion.span
                        className="flex items-center gap-2 justify-center"
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 1, repeat: Infinity }}
                      >
                        <Trophy className="w-5 h-5" />
                        Complete!
                      </motion.span>
                    ) : isCorrect ? (
                      'Continue'
                    ) : (
                      'Got it'
                    )}
                  </MotionButton>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Saving Progress Overlay */}
        {isSaving && (
          <div className="fixed inset-0 bg-bg-main/80 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white rounded-full py-4 px-6 flex items-center gap-3 shadow-md">
              <Loader2 className="w-5 h-5 animate-spin text-accent-purple" />
              <span className="text-title">Saving progress...</span>
            </div>
          </div>
        )}
      </div>
    </MotionConfig>
  );
}
